import Fuse from 'fuse.js';
import { createSelector } from 'reselect';
import { convertStrDateToDMYFormat } from '@beef/shop-common';

import { formatPriceExtended } from 'utils/formatting';
import { getPartnerDetails } from 'store/widgets/PartnerOrder/selectors';

export const getPartnerOrderShow = (state) => getPartnerDetails(state).showPopup || false;
export const getOrderStep = (state) => getPartnerDetails(state).orderStep || 'start';
export const getPartnerProductId = (state) => getPartnerDetails(state).productId || null;
export const getPartnerProductName = (state) => getPartnerDetails(state).productName || '';
export const getPartnerProductPrice = (state) => getPartnerDetails(state).productPrice || '';
export const getPartnerHeadlineTemplate = (state) =>
  getPartnerDetails(state).headlineTemplate || '';
export const getPartnerMessage = (state) => getPartnerDetails(state).message || '';
export const getPartnerStepButtonTitle = (state) => getPartnerDetails(state).stepButtonTitle || '';
export const getPartnerSuccessHeading = (state) => getPartnerDetails(state).successHeading || '';
export const getPartnerSuccessMessage = (state) => getPartnerDetails(state).successMessage || '';
export const getPartnerSuccessMessageReplaced = (state) =>
  getPartnerDetails(state).successMessageReplaced || '';
export const getPartnerErrorHeading = (state) => getPartnerDetails(state).errorHeading || '';
export const getPartnerErrorMessage = (state) => getPartnerDetails(state).errorMessage || '';
export const getPartnerDeliveryError = (state) => getPartnerDetails(state).deliveryError || '';
export const getPartnerAgreement = (state) => getPartnerDetails(state).agreement || '';
export const getDeliveryActiveSlug = (state) => getPartnerDetails(state).deliveryActiveSlug || '';
export const getCourierUrl = (state) => getPartnerDetails(state).courierUrl || '';
export const getPickupUrl = (state) => getPartnerDetails(state).pickupUrl || '';
export const getOrderBuyUrl = (state) => getPartnerDetails(state).orderBuyUrl || '';

// contact form
export const getFormTexts = (state) => getPartnerDetails(state).formTexts || {};
export const getFormHeading = (state) => getFormTexts(state).heading || '';
export const getFormLegal = (state) => getFormTexts(state).legal || '';
export const getDeliveryTypes = (state) => getPartnerDetails(state).deliveryTypes || [];
export const getDeliveryErrors = (state) => getPartnerDetails(state).deliveryErrors || {};

// courier
export const getCourierData = (state) => getPartnerDetails(state).courierData || {};
export const getCourierPaymentMethods = (state) => getCourierData(state).paymentMethods || [];
export const getCourierStartDate = (state) => getCourierData(state).startDate || '';
export const getCourierActiveDate = (state) => getCourierData(state).activeDate || '';
export const getCourierTipsAddress = (state) => getCourierData(state).tipsAddress || [];
export const getCourierPrefix = (state) => getPartnerDetails(state).courierPrefix || '';
export const getCourierAddress = (state) => getCourierData(state).address || '';
export const getCourierDates = (state) => getCourierData(state).dates || [];
export const getCourierLabels = (state) => getPartnerDetails(state).courierLabels || {};

// pickup
export const getPickupPinSettings = (state) => getPartnerDetails(state).pinSettings || {};
export const getPickupData = (state) => getPartnerDetails(state).pickupData || {};
export const getPickupPointsRaw = (state) => getPickupData(state).points || [];
export const getPickupPointsViews = (state) => getPickupData(state).pointsViews || [];
export const getPickupPointsViewState = (state) => getPickupData(state).pointsViewState || '';
export const getPickupSortActiveButton = (state) => getPickupData(state).sortBy || 'address';
export const getPickupActivePointData = (state) => getPickupData(state).activePoint || null;
export const getPickupPointsCollapsed = (state) => getPickupData(state).poinsCollapsed || false;
export const getPickupActivePaymentSlug = (state) => getPickupData(state).activePaymentSlug || null;
export const getPickupPrefix = (state) => getPartnerDetails(state).pickupPrefix || '';
export const getFormMessageTemplate = (state) => getFormTexts(state).messageTemplate || '';
export const getPickupSortReverseState = (state) => getPickupData(state).reverseState || false;
export const getPickupSearchQuery = (state) => getPickupData(state).searchQuery || '';
export const getPickupSearchQueryTrimmed = (state) =>
  getPickupSearchQuery(state).replace(/^\s*(.*?)\s*$/, '$1');

export const getPartnerHeadline = createSelector(
  getPartnerHeadlineTemplate,
  getPartnerProductName,
  (template, name) => template.replace('{productName}', name),
);

export const getCourierDate = createSelector(
  getCourierStartDate,
  getCourierActiveDate,
  (startDate, activeDate) => activeDate || startDate,
);

export const getCourierDatesMapped = createSelector(getCourierDates, (list) =>
  list.map(({ date }) => {
    const convertDate = convertStrDateToDMYFormat(date);
    return { label: convertDate, value: date };
  }),
);

export const getCourierPrice = createSelector(
  getCourierDates,
  getCourierDate,
  (list, activeDate) => {
    const activeItem = list.filter(({ date }) => date === activeDate);
    const activePrice = (activeItem[0] || {}).price || 0;

    return formatPriceExtended(activePrice);
  },
);

export const getDeliveryAvailable = createSelector(
  getCourierData,
  getPickupData,
  (courierData, pickupData) => !!(courierData.slug || pickupData.slug),
);

export const getCourierComplete = createSelector(
  getCourierAddress,
  (address) => address.length > 3,
);

const getPickupPointsFused = createSelector(
  getPickupPointsRaw,
  (list) =>
    new Fuse(list, {
      includeScore: true,
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      tokenize: false,
      distance: 300,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: ['address'],
    }),
);

export const getPickupPointsUnsorted = createSelector(
  getPickupPointsFused,
  getPickupPointsRaw,
  getPickupSearchQueryTrimmed,
  (listFused, listRaw, query) => {
    if (query) {
      const list = listFused.search(query);
      const bestScore = list.reduce((minScore, item) => Math.min(item.score, minScore), 1);
      return list.filter(({ score }) => score <= bestScore * 2).map(({ item }) => item);
    }
    return listRaw;
  },
);

export const getPickupPointsSortFunc = createSelector(
  getPickupSortActiveButton,
  getPickupSortReverseState,
  (sortBy, reverse) => {
    switch (sortBy) {
      case 'address': {
        return (a, b) => {
          const addressFromA = (a.address || 'яяяя').toString().toLowerCase();
          const addressFromB = (b.address || 'яяяя').toString().toLowerCase();
          return reverse ?
              -addressFromA.localeCompare(addressFromB)
            : addressFromA.localeCompare(addressFromB);
        };
      }
      case 'metro': {
        return (a, b) => {
          const metroFromA = ((a.subway || {}).name || 'яяяя').toString().toLowerCase();
          const metroFromB = ((b.subway || {}).name || 'яяяя').toString().toLowerCase();
          return reverse ?
              -metroFromA.localeCompare(metroFromB)
            : metroFromA.localeCompare(metroFromB);
        };
      }
      default:
        return null;
    }
  },
);

export const getPickupPoints = createSelector(
  getPickupPointsUnsorted,
  getPickupPointsSortFunc,
  (listUnsorted, sortFunc) => (sortFunc ? listUnsorted.slice().sort(sortFunc) : listUnsorted),
);

export const getPickupActivePointAddress = createSelector(
  getPickupActivePointData,
  (point) => `Самовывоз с ${(point || {}).address}`,
);

export const getPickupActivePointName = createSelector(
  getPickupActivePointData,
  (point) => (point || {}).name,
);

export const getPickupPaymentMethods = createSelector(
  getPickupActivePointData,
  (point) => (point || {}).paymentMethods || [],
);

export const getPickupActivePaymentName = createSelector(
  getPickupPaymentMethods,
  getPickupActivePaymentSlug,
  (payments, slug) => (payments.filter((payment) => payment.slug === slug)[0] || {}).name || '',
);

export const getActiveDate = createSelector(
  getDeliveryActiveSlug,
  getPickupActivePointData,
  getCourierDate,
  (slug, point, courierDate) => {
    if (slug === 'courier') {
      return courierDate;
    }
    if (slug === 'pickup') {
      return point.minDate;
    }
    return '';
  },
);

export const getActiveAddress = createSelector(
  getDeliveryActiveSlug,
  getPickupActivePointData,
  getCourierAddress,
  (slug, point, courierAddress) => {
    if (slug === 'courier') {
      return courierAddress;
    }
    if (slug === 'pickup') {
      return point.address;
    }
    return '';
  },
);

export const getPickupComplete = createSelector(getPickupActivePointData, (point) => !!point);

export const getPrefix = createSelector(
  getDeliveryActiveSlug,
  getCourierPrefix,
  getPickupPrefix,
  (slug, courierPrefix, pickupPrefix) => {
    if (slug === 'courier') {
      return courierPrefix;
    }
    if (slug === 'pickup') {
      return pickupPrefix;
    }
    return '';
  },
);

export const getFormMessage = createSelector(
  getFormMessageTemplate,
  getPrefix,
  getPartnerProductName,
  getActiveDate,
  getActiveAddress,
  (message, prefix, name, date, address) => {
    const replacer = (value) => {
      const replaceValue = {
        '{prefix}': prefix,
        '{name}': name,
        '{date}': convertStrDateToDMYFormat(date),
        '{address}': address,
      }[value];
      return replaceValue || value;
    };

    return message.replace(/{prefix}|{name}|{date}|{address}/g, replacer);
  },
);

export const getCourierPriceValue = createSelector(
  getCourierDates,
  getCourierActiveDate,
  (list, activeDate) => {
    const activeItem = list.filter(({ date }) => date === activeDate);
    return (activeItem[0] || {}).price || 0;
  },
);

export const getDeliveryOptions = createSelector(
  getCourierData,
  getPickupData,
  getDeliveryActiveSlug,
  (courierData, pickupData, activeSlug) => {
    const options = [];
    if (courierData.slug) {
      options.push({
        slug: 'courier',
        name: 'Доставка',
        isActive: activeSlug === courierData.slug,
      });
    }
    if (pickupData.slug) {
      options.push({ slug: 'pickup', name: 'Самовывоз', isActive: activeSlug === pickupData.slug });
    }
    return options;
  },
);

export const getFinalPrice = createSelector(
  getPartnerProductPrice,
  getCourierPriceValue,
  getDeliveryActiveSlug,
  (price, courierPrice, deliverySlug) => {
    let priceValue = price;
    if (deliverySlug === 'courier') priceValue = price + parseInt(courierPrice, 10);
    return formatPriceExtended(priceValue);
  },
);
